import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";

import { BulkUploadContentComponent } from "@m19-board/shared/bulk-upload-modal/bulk-upload-content.component";
import { BulkImportService, BulkUploadError, ProductGroupCreation, ProductGroupUpdate } from "../bulk-import.service";
import { MODAL_DATA, ModalRef } from "@front/m19-ui";
import { Marketplace, ProductGroup } from "@front/m19-api-client";
import { ProductGroupEx } from "@front/m19-models";
import { AsinService, ProductGroupService } from "@front/m19-services";
import { toSignal } from "@angular/core/rxjs-interop";

export interface ProductGroupBulkUploadModalData {
  accountId: string;
  marketplace: Marketplace;
  productGroups: ProductGroupEx[];
  bulkData: string;
}

@Component({
  selector: "app-product-group-bulk-upload",
  imports: [BulkUploadContentComponent],
  templateUrl: "./product-group-bulk-upload.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupBulkUploadComponent {
  private readonly bulkImportService = inject(BulkImportService);
  private readonly asinService = inject(AsinService);
  private readonly productGroupService = inject(ProductGroupService);

  readonly modalData = inject(MODAL_DATA) as ProductGroupBulkUploadModalData;
  readonly modalRef = inject(ModalRef);
  readonly fields = this.bulkImportService.ProductGroupBulkConfigDescription;
  readonly bulkExample = this.bulkImportService.getProductGroupBulkConfigExample();

  readonly fileUploadError = signal<string | undefined>(undefined);

  private readonly catalog = toSignal(
    this.asinService.getCatalog(this.modalData.accountId, this.modalData.marketplace),
  );

  uploadProductGroups(input: string): void {
    const parsedInput = this.parseInput(input);

    this.modalRef.close({
      bulkData: input,
      creations: parsedInput.creations,
      updates: parsedInput.updates,
      errors: parsedInput.errors.map((error) => ({
        lineIndex: error.lineIndex,
        csvRow: error.csvRow,
        error: this.bulkImportService.FMT_BULK_UPLOAD_ERRORS[error.error],
      })),
    });
  }

  onCsvUpload(files: FileList | null): void {
    if (files && files.length > 0) {
      const csvFile = files[0];
      if (csvFile.type !== "text/csv" && csvFile.type !== "application/vnd.ms-excel") {
        this.fileUploadError.set(`${csvFile.name} is not a CSV file`);
        return;
      }
      const reader: FileReader = new FileReader();
      reader.readAsText(csvFile);
      reader.onload = () => {
        const csv: string = reader.result as string;
        this.uploadProductGroups(csv);
      };
    }
  }

  parseInput(input: string) {
    const errors: { lineIndex: number; csvRow: string; error: BulkUploadError }[] = [];
    const creations: ProductGroupCreation[] = [];
    const updates: ProductGroupUpdate[] = [];
    const rows = input.split(/[\n]+/);
    const productGroupIndex = this.getProductGroupIndex();
    let lineIndex = 0;
    for (const row of rows) {
      const trimmedRow = row.trim();
      if (trimmedRow === "") {
        // discard empty row
        continue;
      }
      lineIndex++;
      const csvRow = this.bulkImportService.parseProductGroupCsvRow(trimmedRow.split(/\t/));
      if (!csvRow) {
        errors.push({ lineIndex: lineIndex, csvRow: trimmedRow, error: BulkUploadError.WRONG_LINE_FORMAT });
        continue;
      }
      if (csvRow.productGroupId === undefined) {
        errors.push({
          lineIndex: lineIndex,
          csvRow: trimmedRow,
          error: BulkUploadError.PRODUCT_GROUP_ID_NOT_AN_INTEGER,
        });
        continue;
      }
      if (csvRow.productGroupId === 0) {
        // product group creation
        const productGroup = {
          accountId: this.modalData.accountId,
          marketplace: this.modalData.marketplace,
          ...csvRow,
        } as ProductGroup;

        // check ASINs are in catalog
        const inexistingAsins = this.checkAsinsAreInCatalog(productGroup.items);
        if (inexistingAsins.length > 0) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.ASIN_NOT_IN_CATALOG,
          });
          continue;
        }
        if (productGroup.items && productGroup.items.length > ProductGroupEx.MaxProductGroupItems) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.CANNOT_HAVE_MORE_THAN_MAX_PRODUCT_GROUP_ITEMS,
          });
          continue;
        }

        // check product group name
        const validNameError = this.productGroupService.isValidProductGroupName(
          productGroup.productGroupName,
          this.modalData.accountId,
          this.modalData.marketplace,
          0,
        );
        if (validNameError != undefined) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.PRODUCT_GROUP_NAME_NOT_VALID,
          });
          continue;
        }

        creations.push({
          lineIndex: lineIndex,
          csvRow: trimmedRow,
          productGroup: productGroup,
        });
      } else {
        // product group update
        if (!productGroupIndex.has(csvRow.productGroupId)) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.PRODUCT_GROUP_DOES_NOT_EXIST,
          });
          continue;
        }
        // check if duplicated product group update
        if (updates.find((u) => u.productGroupToUpdate.productGroupId == csvRow.productGroupId)) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.DUPLICATED_STRATEGY_UPDATE,
          });
          continue;
        }
        const toUpdate = productGroupIndex.get(csvRow.productGroupId)!;
        const updateParams = this.bulkImportService.getProductGroupUpdateParams(csvRow, toUpdate);
        // check if something to update
        if (!this.isUpdating(updateParams)) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.NOTHING_TO_UPDATE,
          });
          continue;
        }

        // check added ASINs
        const inexistingAsins = this.checkAsinsAreInCatalog(updateParams.asinsToAdd);
        if (inexistingAsins.length > 0) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.ASIN_NOT_IN_CATALOG,
          });
          continue;
        }
        if (
          toUpdate.items.length + updateParams.asinsToAdd.length - updateParams.asinsToDelete.length >
          ProductGroupEx.MaxProductGroupItems
        ) {
          errors.push({
            lineIndex: lineIndex,
            csvRow: trimmedRow,
            error: BulkUploadError.CANNOT_HAVE_MORE_THAN_MAX_PRODUCT_GROUP_ITEMS,
          });
          continue;
        }

        // check product group name
        if (updateParams.productGroupName) {
          const validNameError = this.productGroupService.isValidProductGroupName(
            updateParams.productGroupName,
            this.modalData.accountId,
            this.modalData.marketplace,
            0,
          );
          if (validNameError != undefined) {
            errors.push({
              lineIndex: lineIndex,
              csvRow: trimmedRow,
              error: BulkUploadError.PRODUCT_GROUP_NAME_NOT_VALID,
            });
            continue;
          }
        }
        updates.push({
          lineIndex: lineIndex,
          csvRow: trimmedRow,
          productGroupToUpdate: toUpdate,
          updatedFields: updateParams,
        });
      }
    }
    return { creations, updates, errors };
  }

  private isUpdating(
    params: Partial<ProductGroup> & {
      asinsToAdd: string[];
      asinsToDelete: string[];
      asins: string[];
    },
  ) {
    for (const key in params) {
      if (key === "productGroupId" || key === "accountId" || key == "marketplace" || key == "asins" || key == "items")
        continue;
      if (key === "asinsToAdd" || key === "asinsToDelete") {
        if (params[key].length > 0) {
          return true;
        } else {
          continue;
        }
      }
      if ((params as any)[key] !== undefined) return true;
    }
    return false;
  }

  private checkAsinsAreInCatalog(asins: string[] | undefined) {
    const inexistingAsins: string[] = [];
    for (const asin of asins ?? []) {
      if (!this.catalog()?.contains(asin)) {
        inexistingAsins.push(asin);
      }
    }
    return inexistingAsins;
  }

  private getProductGroupIndex() {
    const productGroupIndex = new Map<number, ProductGroupEx>();
    for (const pg of this.modalData.productGroups) {
      productGroupIndex.set(pg.productGroupId, pg);
    }
    return productGroupIndex;
  }
}
